import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, StyleSheet, Animated, Dimensions } from 'react-native';
import {
  Text,
  Card,
  Button,
  useTheme,
  ProgressBar,
  Chip,
  IconButton,
} from 'react-native-paper';
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Target,
  Users,
  Zap,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Minimize2,
  Maximize2,
} from 'lucide-react-native';
import type { ContentRecommendation } from '@coffeeplexity/shared';

interface RecommendationPerformanceTrackerProps {
  metrics: PerformanceMetrics;
  recommendations: ContentRecommendation[];
  onOptimizationSuggestion: (suggestion: OptimizationSuggestion) => void;
  compact?: boolean;
  showDetails?: boolean;
}

interface PerformanceMetrics {
  loadTime: number;
  cacheHitRate: number;
  errorRate: number;
  userEngagement: number;
}

interface OptimizationSuggestion {
  type: 'performance' | 'engagement' | 'accuracy' | 'cache';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'high' | 'medium' | 'low';
  action: string;
  metrics: Record<string, number>;
}

interface PerformanceInsight {
  metric: string;
  value: number;
  trend: 'up' | 'down' | 'stable';
  status: 'good' | 'warning' | 'critical';
  description: string;
  suggestion?: string;
}

const { width: screenWidth } = Dimensions.get('window');

export function RecommendationPerformanceTracker({
  metrics,
  recommendations,
  onOptimizationSuggestion,
  compact = false,
  showDetails = false,
}: RecommendationPerformanceTrackerProps) {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(!compact);
  const [animatedMetrics, setAnimatedMetrics] = useState(metrics);
  const [previousMetrics, setPreviousMetrics] = useState(metrics);
  const [fadeAnim] = useState(new Animated.Value(1));
  const [slideAnim] = useState(new Animated.Value(0));

  // Update animated metrics with smooth transitions
  useEffect(() => {
    setPreviousMetrics(animatedMetrics);

    // Animate metric changes
    Animated.timing(fadeAnim, {
      toValue: 0.7,
      duration: 200,
      useNativeDriver: true,
    }).start(() => {
      setAnimatedMetrics(metrics);
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }).start();
    });
  }, [metrics, fadeAnim]);

  // Calculate performance insights
  const performanceInsights = useMemo((): PerformanceInsight[] => {
    const insights: PerformanceInsight[] = [];

    // Load Time Analysis
    const loadTimeStatus =
      metrics.loadTime < 1000
        ? 'good'
        : metrics.loadTime < 3000
          ? 'warning'
          : 'critical';

    insights.push({
      metric: 'Load Time',
      value: metrics.loadTime,
      trend:
        metrics.loadTime > previousMetrics.loadTime
          ? 'up'
          : metrics.loadTime < previousMetrics.loadTime
            ? 'down'
            : 'stable',
      status: loadTimeStatus,
      description: `Recommendations load in ${metrics.loadTime}ms`,
      suggestion:
        loadTimeStatus !== 'good'
          ? 'Consider implementing request batching or caching'
          : undefined,
    });

    // Cache Hit Rate Analysis
    const cacheStatus =
      metrics.cacheHitRate > 0.8
        ? 'good'
        : metrics.cacheHitRate > 0.5
          ? 'warning'
          : 'critical';

    insights.push({
      metric: 'Cache Hit Rate',
      value: metrics.cacheHitRate,
      trend:
        metrics.cacheHitRate > previousMetrics.cacheHitRate
          ? 'up'
          : metrics.cacheHitRate < previousMetrics.cacheHitRate
            ? 'down'
            : 'stable',
      status: cacheStatus,
      description: `${Math.round(metrics.cacheHitRate * 100)}% of requests served from cache`,
      suggestion:
        cacheStatus !== 'good'
          ? 'Optimize cache TTL policies and prefetching'
          : undefined,
    });

    // Error Rate Analysis
    const errorStatus =
      metrics.errorRate < 0.01
        ? 'good'
        : metrics.errorRate < 0.05
          ? 'warning'
          : 'critical';

    insights.push({
      metric: 'Error Rate',
      value: metrics.errorRate,
      trend:
        metrics.errorRate > previousMetrics.errorRate
          ? 'up'
          : metrics.errorRate < previousMetrics.errorRate
            ? 'down'
            : 'stable',
      status: errorStatus,
      description: `${Math.round(metrics.errorRate * 100)}% of requests fail`,
      suggestion:
        errorStatus !== 'good'
          ? 'Implement better error handling and fallback strategies'
          : undefined,
    });

    // User Engagement Analysis
    const engagementStatus =
      metrics.userEngagement > 0.7
        ? 'good'
        : metrics.userEngagement > 0.4
          ? 'warning'
          : 'critical';

    insights.push({
      metric: 'User Engagement',
      value: metrics.userEngagement,
      trend:
        metrics.userEngagement > previousMetrics.userEngagement
          ? 'up'
          : metrics.userEngagement < previousMetrics.userEngagement
            ? 'down'
            : 'stable',
      status: engagementStatus,
      description: `${Math.round(metrics.userEngagement * 100)}% average user satisfaction`,
      suggestion:
        engagementStatus !== 'good'
          ? 'Improve recommendation relevance and explanation quality'
          : undefined,
    });

    return insights;
  }, [metrics, previousMetrics]);

  // Generate optimization suggestions
  const optimizationSuggestions = useMemo((): OptimizationSuggestion[] => {
    const suggestions: OptimizationSuggestion[] = [];

    // Performance optimization
    if (metrics.loadTime > 2000) {
      suggestions.push({
        type: 'performance',
        title: 'Optimize Load Time',
        description:
          'Recommendation loading is slower than optimal. Consider implementing request batching and caching.',
        impact: 'high',
        effort: 'medium',
        action: 'Implement request batching and improve caching strategy',
        metrics: {
          currentLoadTime: metrics.loadTime,
          targetLoadTime: 1000,
          potentialImprovement: (metrics.loadTime - 1000) / metrics.loadTime,
        },
      });
    }

    // Cache optimization
    if (metrics.cacheHitRate < 0.6) {
      suggestions.push({
        type: 'cache',
        title: 'Improve Cache Efficiency',
        description:
          'Low cache hit rate is causing unnecessary API calls. Optimize caching policies.',
        impact: 'medium',
        effort: 'low',
        action: 'Adjust cache TTL and implement intelligent prefetching',
        metrics: {
          currentHitRate: metrics.cacheHitRate,
          targetHitRate: 0.8,
          potentialSavings: (0.8 - metrics.cacheHitRate) * 100,
        },
      });
    }

    // Engagement optimization
    if (metrics.userEngagement < 0.5) {
      suggestions.push({
        type: 'engagement',
        title: 'Enhance Recommendation Quality',
        description:
          'User engagement with recommendations is below target. Improve relevance and explanations.',
        impact: 'high',
        effort: 'high',
        action:
          'Refine recommendation algorithms and improve explanation quality',
        metrics: {
          currentEngagement: metrics.userEngagement,
          targetEngagement: 0.7,
          potentialIncrease: (0.7 - metrics.userEngagement) * 100,
        },
      });
    }

    // Accuracy optimization
    const lowConfidenceCount = recommendations.filter(
      r => r.confidence < 0.6
    ).length;
    const lowConfidenceRate =
      recommendations.length > 0
        ? lowConfidenceCount / recommendations.length
        : 0;

    if (lowConfidenceRate > 0.3) {
      suggestions.push({
        type: 'accuracy',
        title: 'Improve Recommendation Confidence',
        description:
          'Many recommendations have low confidence scores. Enhance the recommendation model.',
        impact: 'medium',
        effort: 'high',
        action: 'Retrain recommendation models with more user feedback data',
        metrics: {
          lowConfidenceRate,
          targetConfidenceRate: 0.1,
          affectedRecommendations: lowConfidenceCount,
        },
      });
    }

    return suggestions.sort((a, b) => {
      const impactWeight = { high: 3, medium: 2, low: 1 };
      const effortWeight = { low: 3, medium: 2, high: 1 };

      const scoreA = impactWeight[a.impact] + effortWeight[a.effort];
      const scoreB = impactWeight[b.impact] + effortWeight[b.effort];

      return scoreB - scoreA;
    });
  }, [metrics, recommendations]);

  // Handle expand/collapse
  const handleToggleExpanded = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);

    Animated.timing(slideAnim, {
      toValue: newExpanded ? 1 : 0,
      duration: 300,
      useNativeDriver: true,
    }).start();
  }, [isExpanded, slideAnim]);

  // Handle optimization suggestion
  const handleOptimizationSuggestion = useCallback(
    (suggestion: OptimizationSuggestion) => {
      onOptimizationSuggestion(suggestion);
    },
    [onOptimizationSuggestion]
  );

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return '#4ECDC4';
      case 'warning':
        return '#FF6B35';
      case 'critical':
        return '#E74C3C';
      default:
        return theme.colors.onSurfaceVariant;
    }
  };

  // Get trend icon
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return TrendingUp;
      case 'down':
        return TrendingDown;
      default:
        return Activity;
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return CheckCircle;
      case 'warning':
        return AlertTriangle;
      case 'critical':
        return AlertTriangle;
      default:
        return Activity;
    }
  };

  // Render compact view
  if (compact && !isExpanded) {
    return (
      <Card style={styles.compactContainer}>
        <Card.Content style={styles.compactContent}>
          <View style={styles.compactHeader}>
            <View style={styles.compactLeft}>
              <BarChart3 size={16} color={theme.colors.primary} />
              <Text
                variant='bodySmall'
                style={[styles.compactTitle, { color: theme.colors.onSurface }]}
              >
                Performance
              </Text>
            </View>
            <View style={styles.compactMetrics}>
              {performanceInsights.slice(0, 2).map((insight, index) => {
                const StatusIcon = getStatusIcon(insight.status);
                return (
                  <View key={insight.metric} style={styles.compactMetric}>
                    <StatusIcon
                      size={12}
                      color={getStatusColor(insight.status)}
                    />
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.compactMetricText,
                        { color: getStatusColor(insight.status) },
                      ]}
                    >
                      {insight.metric === 'Load Time'
                        ? `${Math.round(insight.value)}ms`
                        : `${Math.round(insight.value * 100)}%`}
                    </Text>
                  </View>
                );
              })}
            </View>
            <IconButton
              icon={Maximize2}
              size={16}
              onPress={handleToggleExpanded}
              style={styles.expandButton}
            />
          </View>
        </Card.Content>
      </Card>
    );
  }

  // Render full view
  return (
    <Animated.View style={{ opacity: fadeAnim }}>
      <Card style={styles.container}>
        <Card.Content>
          {/* Header */}
          <View style={styles.header}>
            <View style={styles.headerLeft}>
              <BarChart3 size={20} color={theme.colors.primary} />
              <Text
                variant='titleMedium'
                style={[styles.title, { color: theme.colors.onSurface }]}
              >
                Performance Metrics
              </Text>
            </View>
            {compact && (
              <IconButton
                icon={Minimize2}
                size={16}
                onPress={handleToggleExpanded}
                style={styles.collapseButton}
              />
            )}
          </View>

          {/* Performance Insights */}
          <View style={styles.insightsContainer}>
            {performanceInsights.map((insight, index) => {
              const TrendIcon = getTrendIcon(insight.trend);
              const StatusIcon = getStatusIcon(insight.status);
              const statusColor = getStatusColor(insight.status);

              return (
                <View key={insight.metric} style={styles.insightCard}>
                  <View style={styles.insightHeader}>
                    <View style={styles.insightLeft}>
                      <StatusIcon size={16} color={statusColor} />
                      <Text
                        variant='bodyMedium'
                        style={[
                          styles.insightTitle,
                          { color: theme.colors.onSurface },
                        ]}
                      >
                        {insight.metric}
                      </Text>
                    </View>
                    <View style={styles.insightRight}>
                      <TrendIcon
                        size={14}
                        color={
                          insight.trend === 'up'
                            ? '#4ECDC4'
                            : insight.trend === 'down'
                              ? '#E74C3C'
                              : theme.colors.onSurfaceVariant
                        }
                      />
                      <Text
                        variant='bodyLarge'
                        style={[styles.insightValue, { color: statusColor }]}
                      >
                        {insight.metric === 'Load Time'
                          ? `${Math.round(insight.value)}ms`
                          : `${Math.round(insight.value * 100)}%`}
                      </Text>
                    </View>
                  </View>

                  <Text
                    variant='bodySmall'
                    style={[
                      styles.insightDescription,
                      { color: theme.colors.onSurfaceVariant },
                    ]}
                  >
                    {insight.description}
                  </Text>

                  {insight.suggestion && (
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.insightSuggestion,
                        { color: theme.colors.primary },
                      ]}
                    >
                      💡 {insight.suggestion}
                    </Text>
                  )}

                  {/* Progress bar for visual representation */}
                  <ProgressBar
                    progress={
                      insight.metric === 'Load Time'
                        ? Math.min(insight.value / 5000, 1) // Max 5 seconds
                        : insight.value
                    }
                    color={statusColor}
                    style={styles.insightProgress}
                  />
                </View>
              );
            })}
          </View>

          {/* Optimization Suggestions */}
          {optimizationSuggestions.length > 0 && showDetails && (
            <View style={styles.suggestionsContainer}>
              <Text
                variant='titleSmall'
                style={[
                  styles.suggestionsTitle,
                  { color: theme.colors.onSurface },
                ]}
              >
                Optimization Suggestions
              </Text>

              {optimizationSuggestions.slice(0, 3).map((suggestion, index) => {
                const impactColor =
                  suggestion.impact === 'high'
                    ? '#E74C3C'
                    : suggestion.impact === 'medium'
                      ? '#FF6B35'
                      : '#4ECDC4';

                const effortColor =
                  suggestion.effort === 'high'
                    ? '#E74C3C'
                    : suggestion.effort === 'medium'
                      ? '#FF6B35'
                      : '#4ECDC4';

                return (
                  <View key={index} style={styles.suggestionCard}>
                    <View style={styles.suggestionHeader}>
                      <Text
                        variant='bodyMedium'
                        style={[
                          styles.suggestionTitle,
                          { color: theme.colors.onSurface },
                        ]}
                      >
                        {suggestion.title}
                      </Text>
                      <View style={styles.suggestionBadges}>
                        <Chip
                          mode='outlined'
                          textStyle={[styles.badgeText, { color: impactColor }]}
                          style={[styles.badge, { borderColor: impactColor }]}
                        >
                          {suggestion.impact} impact
                        </Chip>
                        <Chip
                          mode='outlined'
                          textStyle={[styles.badgeText, { color: effortColor }]}
                          style={[styles.badge, { borderColor: effortColor }]}
                        >
                          {suggestion.effort} effort
                        </Chip>
                      </View>
                    </View>

                    <Text
                      variant='bodySmall'
                      style={[
                        styles.suggestionDescription,
                        { color: theme.colors.onSurfaceVariant },
                      ]}
                    >
                      {suggestion.description}
                    </Text>

                    <Button
                      mode='outlined'
                      onPress={() => handleOptimizationSuggestion(suggestion)}
                      style={styles.suggestionButton}
                      labelStyle={styles.suggestionButtonLabel}
                      icon={Zap}
                      compact
                    >
                      {suggestion.action}
                    </Button>
                  </View>
                );
              })}
            </View>
          )}

          {/* Summary Stats */}
          <View style={styles.summaryContainer}>
            <View style={styles.summaryStats}>
              <View style={styles.summaryStat}>
                <Clock size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.summaryStatText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Avg Load: {Math.round(metrics.loadTime)}ms
                </Text>
              </View>
              <View style={styles.summaryStat}>
                <Target size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.summaryStatText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Cache: {Math.round(metrics.cacheHitRate * 100)}%
                </Text>
              </View>
              <View style={styles.summaryStat}>
                <Users size={16} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='bodySmall'
                  style={[
                    styles.summaryStatText,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Engagement: {Math.round(metrics.userEngagement * 100)}%
                </Text>
              </View>
            </View>
          </View>
        </Card.Content>
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 12,
  },
  compactContainer: {
    marginBottom: 8,
  },
  compactContent: {
    paddingVertical: 8,
  },
  compactHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  compactLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  compactTitle: {
    fontWeight: '500',
    fontSize: 12,
  },
  compactMetrics: {
    flexDirection: 'row',
    gap: 12,
  },
  compactMetric: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  compactMetricText: {
    fontSize: 10,
    fontWeight: '600',
  },
  expandButton: {
    margin: 0,
    padding: 4,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontWeight: '600',
  },
  collapseButton: {
    margin: 0,
    padding: 4,
  },
  insightsContainer: {
    gap: 12,
    marginBottom: 16,
  },
  insightCard: {
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
  },
  insightHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  insightLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  insightTitle: {
    fontWeight: '500',
  },
  insightRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  insightValue: {
    fontWeight: '600',
  },
  insightDescription: {
    marginBottom: 8,
    lineHeight: 16,
  },
  insightSuggestion: {
    marginBottom: 8,
    fontStyle: 'italic',
    lineHeight: 16,
  },
  insightProgress: {
    height: 4,
    borderRadius: 2,
  },
  suggestionsContainer: {
    marginBottom: 16,
  },
  suggestionsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  suggestionCard: {
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.05)',
    marginBottom: 8,
  },
  suggestionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  suggestionTitle: {
    fontWeight: '500',
    flex: 1,
    marginRight: 8,
  },
  suggestionBadges: {
    flexDirection: 'row',
    gap: 4,
  },
  badge: {
    height: 20,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: '600',
  },
  suggestionDescription: {
    marginBottom: 12,
    lineHeight: 16,
  },
  suggestionButton: {
    alignSelf: 'flex-start',
  },
  suggestionButtonLabel: {
    fontSize: 11,
  },
  summaryContainer: {
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.1)',
  },
  summaryStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  summaryStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  summaryStatText: {
    fontSize: 11,
    fontWeight: '500',
  },
});
